import { Box, Link, Menu, MenuItem, ListItemIcon, ListItemText, IconButton } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import { useEffect, useState } from "react";
import { Event } from "../interfaces/Event";
import { deleteEvent, getEvents } from "../api/events";
import { formatDatePtBr } from "../utils/dateUtils";
import ButtonLoading from "../components/ButtonLoading";
import { colors, USER_DATA } from "../utils/constants";
import DialogCreateEventForm from "./DialogCreateEventForm";
import { useParams } from "react-router-dom";
import Loading from "../components/Loading";
import DialogConfirm from "../components/DialogConfirm";
import { toast } from "react-toastify";
import Row from "../components/Row";
import moment from "moment-timezone";
import CardInfo from "../components/CardInfo";
import DialogEventDetails from "./DialogEventDetails";
import DialogQRCode from "./DialogQRCode";
import DialogMakeCheckin from "./DialogMakeCheckin";
import AddIcon from "@mui/icons-material/Add";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

let isMobile = window.innerWidth < 768;

export default function CreateEvent() {
  const [events, setEvents] = useState<Event[]>([]);
  const [openCreateEvent, setOpenCreateEvent] = useState<boolean>(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);
  const [openEventDetails, setOpenEventDetails] = useState<boolean>(false);
  const [openQrCode, setOpenQrCode] = useState<boolean>(false);
  const [openDialogMakeCheckin, setOpenDialogMakeCheckin] =
    useState<boolean>(false);

  const [loading, setLoading] = useState(true);
  const [eventSelected, setEventSelected] = useState<Event | null>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { client_id } = useParams();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = () => {
    setLoading(true);
    getEvents(client_id)
      .then((response: Event[]) => {
        setEvents(response);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    fetchEvents();
    setOpenCreateEvent(false);
    // clean form data and set eventSelected to null
    setEventSelected(null);
  };

  const handleDelete = (id: string) => {
    deleteEvent(id)
      .then(() => {
        toast.success("Evento deletado com sucesso!");
        fetchEvents();
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setOpenConfirmDelete(false);
      });
  };

  // Defina o fuso horário desejado
  const timezone = "America/Sao_Paulo";

  // Obtenha a data e hora atuais no fuso horário especificado
  const now = moment.tz(timezone);

  const activeEventsCount = events.filter((event) => {
    const eventStart = moment.tz(event.dateInit, timezone);
    const eventEnd = moment.tz(event.dateFinal, timezone);
    return eventStart.isSameOrBefore(now) && eventEnd.isSameOrAfter(now);
  }).length;

  const inactiveEventsCount = events.length - activeEventsCount;

  const isEventActive = (event: Event) => {
    const today = moment.tz("America/Sao_Paulo").startOf("day");
    return today.isBefore(event.dateFinal) || today.isSame(event.dateFinal);
  };

  const handleOpenCreateEvent = () => {
    const userData = JSON.parse(localStorage.getItem(USER_DATA) || "{}");

    const testPeriodDate = moment(userData?.test_period).format("DD/MM/YYYY");
    const isPeriodTest = moment().isBefore(moment(userData?.test_period));

    if (userData.subscriptionStatus != "ativo" && !isPeriodTest) {
      toast.error("Voce precisa ter uma assinatura ativa para criar um evento");
      return;
    }

    setOpenCreateEvent(true);
  };

  const handleOpenMenu = (
    e: React.MouseEvent<HTMLElement>,
    event: Event
  ) => {
    setEventSelected(event);
    setMenuAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handlePageChange = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const paginatedEvents = events.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box style={styles.container}>
      <Row
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 24,
          marginTop: 24,
        }}
      >
        <Link
          color="inherit"
          href={`/${client_id}`}
          style={styles.headerLink}
        >
          <SchoolOutlinedIcon sx={{ fontSize: 18 }} /> Ver como aluno
        </Link>
        <Link
          color="inherit"
          href={`/${client_id}/payments`}
          style={styles.headerLink}
        >
          <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18 }} /> Dados de
          pagamento
        </Link>
      </Row>

      {!loading && (
        <Row
          style={{
            display: "flex",
            gap: isMobile ? 12 : 24,
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 24,
            maxWidth: isMobile ? 350 : "auto",
          }}
        >
          <CardInfo label="eventos criados" dataInfo={events.length} />
          <CardInfo label="eventos ativos" dataInfo={activeEventsCount} />
          <CardInfo label="eventos encerrados" dataInfo={inactiveEventsCount} />
        </Row>
      )}

      <Row>
        <ButtonLoading
          label={"Criar evento"}
          endIcon={<AddIcon />}
          className="landing-hover-lift"
          style={styles.createButton}
          onClick={handleOpenCreateEvent}
        />
      </Row>

      {loading ? (
        <Loading />
      ) : (
        <TableContainer component={Paper} sx={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={styles.headCell}>
                  Banner
                </TableCell>
                <TableCell align="center" sx={styles.headCell}>
                  Título
                </TableCell>
                <TableCell align="center" sx={styles.headCell}>
                  Descrição
                </TableCell>
                <TableCell align="center" sx={styles.headCell}>
                  Início
                </TableCell>
                <TableCell align="center" sx={styles.headCell}>
                  Final
                </TableCell>
                <TableCell align="center" sx={styles.headCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEvents.map((event) => (
                <TableRow key={event.id} sx={styles.bodyRow}>
                  <TableCell align="center" style={{ width: 160 }}>
                    <img style={styles.bannerThumb} src={event.banner} alt={event.title} />
                  </TableCell>
                  <TableCell align="center">
                    <Row style={styles.eventTitle}>
                      <Box
                        style={{
                          ...styles.statusDot,
                          backgroundColor: isEventActive(event)
                            ? colors.status.green
                            : colors.status.red,
                        }}
                      />
                      {event.title}
                    </Row>
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ whiteSpace: "pre-line", maxWidth: 380 }}
                  >
                    <span style={styles.description}>{event.subtitle}</span>
                  </TableCell>
                  <TableCell align="center">
                    <span style={styles.dateText}>
                      {formatDatePtBr(new Date(event.dateInit))}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <span style={styles.dateText}>
                      {formatDatePtBr(new Date(event.dateFinal))}
                    </span>
                  </TableCell>
                  <TableCell align="center" width={60}>
                    <IconButton
                      onClick={(e) => handleOpenMenu(e, event)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={events.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Eventos por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count}`
            }
          />
        </TableContainer>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: "220px" } }}
      >
        <MenuItem
          onClick={() => {
            setOpenDialogMakeCheckin(true);
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <PeopleAltOutlinedIcon sx={{ fontSize: 20, color: colors.primary }} />
          </ListItemIcon>
          <ListItemText>Fazer checkin para aluno</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenQrCode(true);
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <QrCode2Icon sx={{ fontSize: 20, color: colors.primary }} />
          </ListItemIcon>
          <ListItemText>Ver QR Code do evento</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenCreateEvent(true);
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon sx={{ fontSize: 20, color: colors.primary }} />
          </ListItemIcon>
          <ListItemText>Editar evento</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenEventDetails(true);
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <VisibilityOutlinedIcon sx={{ fontSize: 20, color: colors.primary }} />
          </ListItemIcon>
          <ListItemText>Ver dados do evento</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenConfirmDelete(true);
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon sx={{ fontSize: 20, color: colors.status.red }} />
          </ListItemIcon>
          <ListItemText sx={{ color: colors.status.red }}>
            Deletar evento
          </ListItemText>
        </MenuItem>
      </Menu>

      <DialogMakeCheckin
        open={openDialogMakeCheckin}
        setOpen={() => setOpenDialogMakeCheckin(false)}
        event={eventSelected}
      />

      <DialogQRCode
        open={openQrCode}
        setOpen={() => setOpenQrCode(false)}
        event={eventSelected}
      />

      <DialogCreateEventForm
        close={handleClose}
        open={openCreateEvent}
        event={eventSelected}
      />

      <DialogConfirm
        open={openConfirmDelete}
        setOpen={setOpenConfirmDelete}
        onResponse={(responseOk) =>
          responseOk && eventSelected && handleDelete(eventSelected.id)
        }
        entity="esse evento"
      />

      {openEventDetails && eventSelected ? (
        <DialogEventDetails
          close={() => setOpenEventDetails(false)}
          open={openEventDetails}
          event={eventSelected}
        />
      ) : null}
    </Box>
  );
}

const styles: any = {
  container: {
    paddingTop: 100,
    paddingBottom: 40,
    marginLeft: isMobile ? 16 : 80,
    marginRight: isMobile ? 16 : 80,
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },

  headerLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
    fontWeight: 600,
    color: colors.primary,
    textDecoration: "none",
  },

  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#1A1A1AFF",
    marginTop: 6,
  },

  eventTitle: {
    gap: 10,
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    color: "#14213D",
  },

  createButton: {
    width: 200,
    height: 50,
    marginTop: 24,
    borderRadius: 100,
  },

  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },

  bannerThumb: {
    width: 140,
    height: 78,
    objectFit: "cover",
    borderRadius: 10,
    border: "1px solid #E5E9F0",
    display: "block",
    margin: "0 auto",
  },

  description: {
    fontSize: 14,
    color: "#5B6472",
  },

  dateText: {
    fontSize: 14,
    color: "#5B6472",
    fontWeight: 500,
  },

  tableContainer: {
    marginTop: "40px",
    marginBottom: "40px",
    borderRadius: "16px",
    border: "1.5px solid #E5E9F0",
    boxShadow: "0 10px 24px -16px rgba(20,33,61,.16)",
  },

  headCell: {
    backgroundColor: "#F7F9FC",
    color: "#5B6472",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: "1.5px solid #E5E9F0",
  },

  bodyRow: {
    "&:hover": {
      backgroundColor: "#F7F9FC",
    },
    "&:last-child td": {
      borderBottom: 0,
    },
  },
};
